import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import {
  Scene,
  PerspectiveCamera,
  Mesh,
  LoadingManager,
  AmbientLight,
  Box3,
  Vector3,
  sRGBEncoding,
  PMREMGenerator,
  Object3D,
} from "three";
import {
  disposeComplete,
  loaderUrlModifierForTextures,
  setTextureProperties,
} from "../util/three-utils";
import OuterScene from "./OuterScene";
import { BasisTextureLoader } from "three/examples/jsm/loaders/BasisTextureLoader";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader.js";
import { USE_BASIS_TEXTURES } from "../App";
import { timeoutsShape } from "react-transition-group/cjs/utils/PropTypes";

export default class ViewerScene extends Scene {
  private modelSrc: string;
  public camera: PerspectiveCamera = new PerspectiveCamera(
    75,
    window.innerWidth / window.innerHeight,
    0.1,
    20000
  );
  private outerScene: OuterScene = new OuterScene();
  private cameraControls: OrbitControls;
  private loadingManager = new LoadingManager();
  private gltfLoader = new GLTFLoader(this.loadingManager);
  private basisLoader = new KTX2Loader(this.loadingManager);
  private dracoLoader = new DRACOLoader(this.loadingManager);
  private isDisposed = false;

  constructor(modelSrc: string, domElement: HTMLElement) {
    super();

    this.cameraControls = new OrbitControls(this.camera, domElement);
    this.cameraControls.enableDamping = true;
    this.cameraControls.autoRotate = true;
    this.cameraControls.enablePan = false;
    this.cameraControls.enableZoom = false;
    this.cameraControls.maxPolarAngle = Math.PI * 0.75;
    this.cameraControls.minPolarAngle = Math.PI * 0.25;
    this.cameraControls.minDistance = 1;
    this.cameraControls.maxDistance = 1.85;
    this.cameraControls.autoRotateSpeed = 1;
    this.cameraControls.enableKeys = true;

    this.dracoLoader.setDecoderPath("libs/draco/");
    this.dracoLoader.setDecoderConfig({ type: "js" });
    this.gltfLoader.setDRACOLoader(this.dracoLoader);

    if (USE_BASIS_TEXTURES) {
      this.basisLoader.setTranscoderPath("libs/basis/");
      this.basisLoader.detectSupport((window as any).webGlView.renderer);
      this.loadingManager.addHandler(/\.(jpg|jpeg|png)$/i, this.basisLoader);
    }

    this.modelSrc = modelSrc;
    // this.add(this.outerScene);

    // const pmremGenerator = new PMREMGenerator(
    //   (window as any).webGlViewLookViewer.renderer
    // );
    // this.environment = pmremGenerator.fromScene(
    //   this.outerScene.sky as unknown as Scene
    // ).texture;
    // pmremGenerator.dispose();

    this.loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {};
    this.loadingManager.setURLModifier(loaderUrlModifierForTextures);
  }

  public async init() {
    const model = await this.gltfLoader.loadAsync(this.modelSrc);
    if (this.isDisposed) return;
    const boundingBox = new Box3().setFromObject(model.scene);
    const size = boundingBox.getSize(new Vector3());
    // model.scene.position.y -= size.y * 0.5;
    const nullObject = new Object3D();
    nullObject.position.y = size.y * 0.5;
    model.scene.add(nullObject);
    this.add(model.scene);

    setTextureProperties(this, {
      encoding: sRGBEncoding,
    });

    this.add(this.camera);
    this.cameraControls.target = nullObject.position;
    this.camera.position.z = 1.5;
    this.camera.position.y = size.y * 0.5;
  }

  public async dispose(): Promise<void> {
    this.isDisposed = true;
    this.dracoLoader.dispose();
    this.basisLoader.dispose();
    this.loadingManager.onProgress = null;
    this.cameraControls.dispose();
    disposeComplete(this);
  }

  public update(delta: number): void {
    this.cameraControls.update();
  }

  public onResize(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }
}
